import { z } from "zod";
import { metadata } from "../page";

export enum SourcingRequestStatuses {
  DRAFT = "DRAFT",
  UNPAID = "UNPAID",
  PREPAID = "PREPAID",
  PROCESSING = "PROCESSING",
  PROCESSED = "PROCESSED",
  ACCEPTED = "ACCEPTED",
  REJECTED = "REJECTED",
  QUOTE_AVAILABLE = "QUOTE_AVAILABLE",
  QUOTE_EXPIRED = "QUOTE_EXPIRED",
  PAID = "PAID",
}

export enum PaymentProvider {
  PAYSTACK = "PAYSTACK",
  FLUTTERWAVE = "FLUTTERWAVE",
}

export enum UserType {
  BUSINESS = "BUSINESS",
  SOURCING_AGENT = "SOURCING_AGENT",
}

export enum AssetType {
  IMAGE = "image",
  PDF = "pdf",
  VIDEO = "video",
}

export const assetSchema = z.object({
  downloadUrl: z.string().optional(),
  kind: z.nativeEnum(AssetType),
  assetStorageKey: z.string(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export enum ResolvedStatus {
  REFUNDED_REQUESTED = "REFUNDED_REQUESTED",
  ADDED_TO_NEXT_FINAL_PAYMENT = "ADDED_TO_NEXT_FINAL_PAYMENT",
  PAID_EXTRA_CHARGE = "PAID_EXTRA_CHARGE",
  NOT_RESOLVED = "NOT_RESOLVED",
  WAIVED = "WAIVED",
  VOIDED = "VOIDED",
}

export const extraChargePostOrderSchema = z.object({
  resolvedStatus: z.nativeEnum(ResolvedStatus),
  amountInNaira: z.number().optional(),
  reason: z.string(),
  metadata: z.record(z.any()).optional(),
  createdAt: z.string(),
  _id: z.string().optional(),
});

export const sourcingRequestItemsSchema = z.object({
  productId: z.string().optional(),
  quantity: z.number().min(1, "Enter a valid quantity"),
  unit: z.string().optional(),
  name: z.string().min(1, "Enter a product name"),
  unitPrice: z.number().optional(),
  suggestedPrice: z.number().optional(),
  additionalInformation: z.string().optional(),
  description: z.string().min(1, "Give a description of this product"),
  assets: z.array(assetSchema),
  productLinks: z.array(z.string()).optional(),
  externalProductImageUrl: z.string().optional(),
  price: z.number().optional(),
  _id: z.string().optional(),
});

export const commentSchema = z.object({
  comment: z.string(),
  action: z.string().optional(),
  assets: z.array(assetSchema).optional(),
  createdAt: z.string(),
  createdBy: z.object({
    userType: z.nativeEnum(UserType),
    id: z.string().optional(),
  }),
});

export const newCommentSchema = z.object({
  comment: z.string(),
  action: z.string(),
  assets: z.array(assetSchema).optional(),
});

export const refundRequestSchema = z.object({
  reason: z.string(),
  amount: z.number(),
  metadata: z.record(z.any()).optional(),
  createdAt: z.string(),
});

export const sourcingRequestSchema = z.object({
  _id: z.string(),
  id: z.string(),
  quoteId: z.string(),
  status: z.nativeEnum(SourcingRequestStatuses),
  businessId: z.string(),
  sourcingAgentId: z.string(),
  items: z.array(sourcingRequestItemsSchema),
  shipment: z.any().nullable(),
  shippingType: z.string().optional(),
  deliveryAddress: z.string().optional(),
  title: z.string(),
  type: z.string(),
  prepaymentAmount: z.number(),
  createdAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid date format",
  }),
  updatedAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid date format",
  }),
  attachments: z.array(assetSchema),
  extraChargePostOrder: extraChargePostOrderSchema.optional(),
  comments: z.array(commentSchema),
  notified: z.boolean().optional(),
  viewed: z.boolean().optional(),
  importedProducts: z.array(z.string()).optional(),
  refund: refundRequestSchema.optional(),
  __v: z.number(),
});

export const newSourcingRequestSchema = z.object({
  title: z.string().optional(),
  items: z.array(sourcingRequestItemsSchema),
  attachments: z.array(assetSchema).optional(),
  viewed: z.boolean().optional(),
});

export const verifyPayment = z.object({
  reference: z.string(),
  provider: z.nativeEnum(PaymentProvider),
});

export type SourcingRequestSchema = z.infer<typeof sourcingRequestSchema>;
export type NewSourcingRequestSchema = z.infer<typeof newSourcingRequestSchema>;
export type SourcingRequestItemsSchema = z.infer<
  typeof sourcingRequestItemsSchema
>;
export type VerifyPayment = z.infer<typeof verifyPayment>;
export type AssetSchema = z.infer<typeof assetSchema>;
export type Comment = z.infer<typeof commentSchema>;
export type NewComment = z.infer<typeof newCommentSchema>;

export type ExternalProduct = {
  _id: string;
  name: string;
  supplierId: string;
  minimumOrderQuantity: number;
  productLink: string;
  externalId: string;
  images: string[];
};
